import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import ProjectWorkflowFoundationApi from "../../../../../../infrastructure/api/projectWorkflowFoundation";
import { toJalaliFullDate } from "../../../../../middleware/middleware";
import {
  buildInitialSiteVisitAssignmentPayload,
  clearAssigneeOnPerformerTypeChange,
  CREDIT_VALIDATION_PENDING_MESSAGE,
  getAssigneeFieldLabel,
  getPerformerTypeLabel,
  INITIAL_SITE_VISIT_PERFORMER,
  formatContractorLookupLabel,
  lookupOptionTitle,
  PERFORMER_TYPE_OPTIONS,
  pickActiveSiteVisitAssignment,
  resolveAssigneeIdFromAssignment,
  resolveCanAssignSiteVisit,
  resolvePerformerTypeFromAssignment,
  validateInitialSiteVisitForm,
  isOperatorSiteVisitAssignmentEditable,
  ASSIGNMENT_LOCKED_AFTER_ACCEPTANCE_MESSAGE,
} from "./initialSiteVisitAssignmentUtils";

const InitialSiteVisitAssignmentCard = ({
  requestInfoId,
  workflowSource = "SolarentOperator",
  loanMehrsanGate,
  canEdit = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingPerformers, setLoadingPerformers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [performers, setPerformers] = useState([]);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [formMode, setFormMode] = useState(false);
  const [performerType, setPerformerType] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState("");

  const canAssignSiteVisit = resolveCanAssignSiteVisit(loanMehrsanGate);
  const hasExistingAssignment = Boolean(activeAssignment);
  const assignmentEditable = isOperatorSiteVisitAssignmentEditable(activeAssignment);
  const canEditAssignment = canEdit && assignmentEditable;
  const showSummary = hasExistingAssignment && !formMode;
  const showForm = canEditAssignment && !showSummary && (formMode || !hasExistingAssignment);
  const assigneeFieldLabel = getAssigneeFieldLabel(performerType);

  const applyAssignmentToForm = useCallback((assignment) => {
    const resolvedPerformerType = resolvePerformerTypeFromAssignment(assignment);
    setPerformerType(resolvedPerformerType);
    setAssigneeId(resolveAssigneeIdFromAssignment(assignment, resolvedPerformerType));
    setNote(assignment?.note ?? assignment?.Note ?? "");
    setFormError("");
  }, []);

  const loadPerformers = useCallback(
    async (nextPerformerType) => {
      if (!nextPerformerType) {
        setPerformers([]);
        return;
      }

      setLoadingPerformers(true);
      try {
        const res = await ProjectWorkflowFoundationApi.getInitialSiteVisitPerformerLookups(
          workflowSource,
          nextPerformerType
        );
        setPerformers(res?.data?.performers ?? res?.data?.Performers ?? []);
      } catch {
        setPerformers([]);
      } finally {
        setLoadingPerformers(false);
      }
    },
    [workflowSource]
  );

  const loadData = useCallback(async () => {
    if (!requestInfoId) return;
    setLoading(true);
    try {
      const assignmentsRes = await ProjectWorkflowFoundationApi.getAssignmentsByRequestId(
        workflowSource,
        requestInfoId
      );

      const assignment = pickActiveSiteVisitAssignment(assignmentsRes?.data);
      setActiveAssignment(assignment);
      applyAssignmentToForm(assignment);
      setFormMode(false);
      setPerformers([]);
    } catch {
      setActiveAssignment(null);
      setPerformers([]);
      setPerformerType("");
      setAssigneeId("");
      setNote("");
    } finally {
      setLoading(false);
    }
  }, [applyAssignmentToForm, requestInfoId, workflowSource]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!showForm || !performerType) return;
    loadPerformers(performerType);
  }, [loadPerformers, performerType, showForm]);

  const summaryRows = useMemo(() => {
    if (!activeAssignment) return [];
    const resolvedPerformerType = resolvePerformerTypeFromAssignment(activeAssignment);
    const resolvedAssigneeId = resolveAssigneeIdFromAssignment(
      activeAssignment,
      resolvedPerformerType
    );
    return [
      {
        label: "مجری بازدید",
        value: getPerformerTypeLabel(resolvedPerformerType),
      },
      {
        label:
          resolvedPerformerType === INITIAL_SITE_VISIT_PERFORMER.CONTRACTOR
            ? "پیمانکار"
            : "ناظر",
        value:
          activeAssignment.assignedPartyName ??
          activeAssignment.AssignedPartyName ??
          lookupOptionTitle(performers, resolvedAssigneeId),
      },
      {
        label: "وضعیت ارجاع",
        value:
          activeAssignment.projectAssignmentStatusTitle ??
          activeAssignment.ProjectAssignmentStatusTitle ??
          "—",
      },
      {
        label: "تاریخ ارجاع",
        value: toJalaliFullDate(
          activeAssignment.assignedAt ?? activeAssignment.AssignedAt
        ),
      },
      {
        label: "توضیحات",
        value: activeAssignment.note ?? activeAssignment.Note ?? "—",
      },
    ];
  }, [activeAssignment, performers]);

  const handlePerformerTypeChange = (event) => {
    const nextPerformerType = event.target.value;
    setPerformerType(nextPerformerType);
    setAssigneeId((current) =>
      clearAssigneeOnPerformerTypeChange({
        previousPerformerType: performerType,
        nextPerformerType,
        assigneeId: current,
      })
    );
    setFormError("");
    setPerformers([]);
  };

  const handleSubmit = async () => {
    const validationError = validateInitialSiteVisitForm({ performerType, assigneeId });
    if (validationError) {
      setFormError(validationError);
      toast.error(validationError);
      return;
    }

    setFormError("");
    setSaving(true);
    try {
      const res = await ProjectWorkflowFoundationApi.upsertInitialSiteVisitAssignment(
        workflowSource,
        buildInitialSiteVisitAssignmentPayload({
          requestId: requestInfoId,
          performerType,
          assigneeId,
          note,
        })
      );

      if (!res.ok) {
        toast.error(res.message || "ثبت ارجاع بازدید ناموفق بود.");
        return;
      }

      toast.success(res.message || "ارجاع بازدید با موفقیت ثبت شد.");
      await loadData();
    } catch (err) {
      toast.error(err?.message || "ثبت ارجاع بازدید ناموفق بود.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setFormMode(false);
    applyAssignmentToForm(activeAssignment);
    const resolvedPerformerType = resolvePerformerTypeFromAssignment(activeAssignment);
    if (resolvedPerformerType) {
      loadPerformers(resolvedPerformerType);
    } else {
      setPerformers([]);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <PersonSearchOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>
            ارجاع اولیه بازدید
          </Typography>
        </Stack>

        {loading ? (
          <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={22} />
          </Box>
        ) : null}

        {!loading && !canAssignSiteVisit ? (
          <Alert severity="info" variant="outlined">
            {loanMehrsanGate?.siteVisitAssignmentBlockReason ??
              loanMehrsanGate?.SiteVisitAssignmentBlockReason ??
              CREDIT_VALIDATION_PENDING_MESSAGE}
          </Alert>
        ) : null}

        {!loading && canAssignSiteVisit && showSummary ? (
          <Stack spacing={1.5}>
            <Grid container spacing={1.5}>
              {summaryRows.map((row) => (
                <Grid item xs={12} sm={6} key={row.label}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {row.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {row.value || "—"}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            {canEditAssignment ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                onClick={() => setFormMode(true)}
                sx={{ alignSelf: "flex-start" }}
              >
                ویرایش ارجاع بازدید
              </Button>
            ) : null}
            {canEdit && hasExistingAssignment && !assignmentEditable ? (
              <Alert severity="info" variant="outlined">
                {ASSIGNMENT_LOCKED_AFTER_ACCEPTANCE_MESSAGE}
              </Alert>
            ) : null}
          </Stack>
        ) : null}

        {!loading && canAssignSiteVisit && showForm ? (
          <Stack spacing={1.5}>
            <FormControl fullWidth size="small" error={Boolean(formError) && !performerType}>
              <InputLabel id="initial-site-visit-performer-type-label">
                مجری بازدید اولیه
              </InputLabel>
              <Select
                labelId="initial-site-visit-performer-type-label"
                value={performerType}
                label="مجری بازدید اولیه"
                onChange={handlePerformerTypeChange}
              >
                {PERFORMER_TYPE_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              disabled={!performerType || loadingPerformers}
              error={Boolean(formError) && performerType && !assigneeId}
            >
              <InputLabel id="initial-site-visit-assignee-label">
                {assigneeFieldLabel}
              </InputLabel>
              <Select
                labelId="initial-site-visit-assignee-label"
                value={assigneeId}
                label={assigneeFieldLabel}
                onChange={(event) => {
                  setAssigneeId(event.target.value);
                  setFormError("");
                }}
              >
                {(performers || []).map((item) => (
                  <MenuItem
                    key={item.id ?? item.Id}
                    value={String(item.id ?? item.Id)}
                  >
                    {formatContractorLookupLabel(item)}
                  </MenuItem>
                ))}
              </Select>
              {loadingPerformers ? (
                <FormHelperText>در حال بارگذاری فهرست مجریان...</FormHelperText>
              ) : null}
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="توضیحات (اختیاری)"
              multiline
              minRows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />

            {formError ? (
              <Typography variant="caption" color="error">
                {formError}
              </Typography>
            ) : null}

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                disabled={saving || !performerType || !assigneeId}
                onClick={handleSubmit}
              >
                {saving ? <CircularProgress size={16} color="inherit" /> : null}
                ثبت ارجاع بازدید
              </Button>
              {hasExistingAssignment ? (
                <Button variant="text" size="small" onClick={handleCancelEdit}>
                  انصراف
                </Button>
              ) : null}
            </Stack>
          </Stack>
        ) : null}

        {!loading && canAssignSiteVisit && !canEdit && !hasExistingAssignment ? (
          <Alert severity="warning" variant="outlined">
            دسترسی ویرایش برای ثبت ارجاع بازدید فعال نیست.
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
};

InitialSiteVisitAssignmentCard.propTypes = {
  requestInfoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  workflowSource: PropTypes.string,
  loanMehrsanGate: PropTypes.object,
  canEdit: PropTypes.bool,
};

export default InitialSiteVisitAssignmentCard;
